const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function parseWeekdays(value) {
  if (value === undefined || value === null || value === "") return [];
  const parts = Array.isArray(value) ? value.map(String) : String(value).split(/[,\s]+/);
  const out = [];
  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const named = WEEKDAY_SHORT.findIndex(
      (name) => name.toLowerCase() === trimmed.toLowerCase().slice(0, 3),
    );
    if (named >= 0) {
      out.push(named);
      continue;
    }
    const n = Number.parseInt(trimmed, 10);
    if (Number.isFinite(n) && n >= 0 && n <= 6) out.push(n);
  }
  return [...new Set(out)].sort();
}

export function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function startOfWeek(date) {
  const d = startOfDay(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  return d;
}

export function startOfMonth(date) {
  const d = startOfDay(date);
  d.setDate(1);
  return d;
}

export function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function parseTodoDue(todo) {
  if (!todo?.date_due) return null;
  const parsed = new Date(todo.date_due);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function isOpenTodo(todo) {
  return !todo.complete && Number(todo.complete) !== 1;
}

function atLocalTime(day, hour, minute) {
  const d = startOfDay(day);
  d.setHours(hour ?? 9, minute ?? 0, 0, 0);
  return d;
}

/** Calendar placements for tasks in [rangeStart, rangeEnd]. */
export function occurrencesInRange(todo, rangeStart, rangeEnd) {
  if (!isOpenTodo(todo)) return [];
  const out = [];
  const weekdays = parseWeekdays(todo.repeat_weekdays);
  const weekly = Number(todo.repeating) >= 10080 && weekdays.length > 0;
  const due = parseTodoDue(todo);

  if (weekly) {
    for (let cursor = startOfDay(rangeStart); cursor <= rangeEnd; cursor = addDays(cursor, 1)) {
      if (!weekdays.includes(cursor.getDay())) continue;
      const hour = todo.repeat_hour ?? due?.getHours() ?? 9;
      const minute = todo.repeat_minute ?? due?.getMinutes() ?? 0;
      const at = atLocalTime(cursor, hour, minute);
      out.push({ todo, at, repeating: true });
    }
    return out;
  }

  if (due && due >= rangeStart && due <= rangeEnd) {
    out.push({ todo, at: due, repeating: false });
  }
  return out;
}

export function formatDueFriendly(date, { now = new Date() } = {}) {
  if (!date) return "No due date";
  const today = startOfDay(now);
  const target = startOfDay(date);
  const diffDays = Math.round((target - today) / 86_400_000);
  const time = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (diffDays === 0) return `Today · ${time}`;
  if (diffDays === 1) return `Tomorrow · ${time}`;
  if (diffDays === -1) return `Yesterday · ${time}`;
  if (diffDays > 1 && diffDays < 7) {
    return `${date.toLocaleDateString(undefined, { weekday: "short" })} · ${time}`;
  }
  return `${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })} · ${time}`;
}

export function formatDuration(minutes) {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}
